/**
 * ScreenerDialog — HEY-style sender screening. Shows EVERY connected account's
 * screener settings in one scrollable pane: a per-account opt-in toggle and,
 * when enabled, its first-time senders to approve/reject. When enabled, the
 * inbox hides pending/rejected senders until approved.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Eye, Loader2 } from 'lucide-react';
import { api, type EmailConnection } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import {
  useScreenedSenders,
  useDecideSender,
  useSetScreenerEnabled,
  useSetScreenerFirst,
} from '../hooks/useScreening';
import { useEmailFolders, useScreenerMessages } from '../hooks/useMailbox';
import { useEmailConnections } from '../hooks/useConnectAccount';
import { EmailHtmlBody } from './EmailHtmlBody';

interface ScreenerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ScreenerDialog({ open, onOpenChange }: ScreenerDialogProps) {
  const { data: connections } = useEmailConnections();
  const accounts = connections ?? [];
  // Peek at a pending sender's latest email to judge legitimacy before deciding.
  const [preview, setPreview] = useState<{ accountId: string; senderAddress: string } | null>(null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Screener</DialogTitle>
        </DialogHeader>
        {accounts.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Connect an account to use the screener.
          </p>
        ) : (
          <div className="space-y-3">
            {accounts.map((account) => (
              <AccountScreenerSection
                key={account.accountId}
                account={account}
                open={open}
                onPreview={(senderAddress) =>
                  setPreview({ accountId: account.accountId, senderAddress })
                }
              />
            ))}
          </div>
        )}
      </DialogContent>
      <ScreenerPreviewDialog
        open={preview !== null}
        onOpenChange={(o) => !o && setPreview(null)}
        accountId={preview?.accountId}
        senderAddress={preview?.senderAddress ?? null}
      />
    </Dialog>
  );
}

/**
 * One account's screener settings — the opt-in toggle plus, when enabled, its
 * first-time senders to approve/reject (with a held-message count). Screening is
 * a PER-ACCOUNT opt-in, so the enabled state comes from each account's own
 * settings and the per-account hooks below only fetch for the enabled ones.
 */
function AccountScreenerSection({
  account,
  open,
  onPreview,
}: {
  account: EmailConnection;
  open: boolean;
  onPreview: (senderAddress: string) => void;
}) {
  const enabled = account.modules?.screenerEnabled === true;
  const screenerFirst = account.modules?.screenerFirst === true;
  const { data: senders, isLoading } = useScreenedSenders(
    account.accountId,
    'pending',
    open && enabled,
  );
  // Held-message count alongside the sender count — "3 senders · 8 messages held".
  const { data: folders } = useEmailFolders(open && enabled ? account.accountId : undefined);
  const inboxFolderId = folders?.find((f) => f.systemRole === 'inbox')?.folderId;
  const { data: heldMessages } = useScreenerMessages(
    account.accountId,
    inboxFolderId,
    open && enabled,
  );
  const senderCount = senders?.length ?? 0;
  const messageCount = heldMessages?.length ?? 0;
  const decide = useDecideSender(account.accountId);
  const setEnabled = useSetScreenerEnabled(account.accountId);
  const setFirst = useSetScreenerFirst(account.accountId);

  return (
    <div className="space-y-2 rounded-md border p-3">
      <div className="truncate text-sm font-medium">{account.emailAddress}</div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={enabled}
          disabled={setEnabled.isPending}
          onChange={(e) => setEnabled.mutate(e.target.checked)}
        />
        Screen first-time senders (hide from inbox until approved)
      </label>
      {enabled && (
        <label className="flex items-start gap-2 pl-5 text-xs text-muted-foreground">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={screenerFirst}
            disabled={setFirst.isPending}
            onChange={(e) => setFirst.mutate(e.target.checked)}
          />
          <span>
            Screen new senders before the AI spam filter — hold them here for review instead of
            letting the spam filter act first.
          </span>
        </label>
      )}

      {!enabled ? (
        <p className="text-sm text-muted-foreground">
          Turn on screening to review first-time senders here.
        </p>
      ) : isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      ) : !senders || senders.length === 0 ? (
        <p className="py-2 text-center text-sm text-muted-foreground">No senders to screen.</p>
      ) : (
        <>
          <p className="text-xs font-medium text-foreground">
            {senderCount} sender{senderCount === 1 ? '' : 's'} · {messageCount} message
            {messageCount === 1 ? '' : 's'} held
          </p>
          <p className="text-xs text-muted-foreground">
            Approve lets a sender into your inbox. Reject moves all of their mail to Trash. You can
            also review these in the Screener inbox.
          </p>
          <ul className="space-y-1">
            {senders.map((s) => (
              <li
                key={s.screenedId}
                className="flex items-center justify-between gap-2 rounded border px-2 py-1 text-sm"
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium">
                    {s.senderName || s.senderAddress}
                  </span>
                  {s.senderName && (
                    <span className="block truncate text-xs text-muted-foreground">
                      {s.senderAddress}
                    </span>
                  )}
                </span>
                <span className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    title="Preview latest email"
                    aria-label={`Preview latest email from ${s.senderAddress}`}
                    onClick={() => onPreview(s.senderAddress)}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={decide.isPending}
                    onClick={() =>
                      decide.mutate({ screenedId: s.screenedId, decision: 'approved' })
                    }
                  >
                    Approve
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={decide.isPending}
                    onClick={() =>
                      decide.mutate({ screenedId: s.screenedId, decision: 'rejected' })
                    }
                  >
                    Reject
                  </Button>
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/**
 * Loads and renders the latest synced email from a sender (searched by `from`)
 * so the user can judge legitimacy before approving/rejecting in the screener.
 */
function ScreenerPreviewDialog({
  open,
  onOpenChange,
  accountId,
  senderAddress,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountId?: string;
  senderAddress: string | null;
}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.email.mailbox.screenerPreview(accountId ?? '', senderAddress ?? ''),
    enabled: open && !!senderAddress,
    queryFn: async () => {
      const { messages } = await api.searchEmailMailbox(
        senderAddress as string,
        accountId,
        ['from'],
        undefined,
        undefined,
        1,
      );
      if (messages.length === 0) return null;
      return api.getEmailMailboxMessage(messages[0].messageId);
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="truncate">Latest from {senderAddress}</DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : isError ? (
          <p className="py-6 text-center text-sm text-destructive">Couldn’t load a preview.</p>
        ) : !data ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            No synced messages from this sender yet.
          </p>
        ) : (
          <div className="space-y-2">
            <div className="text-sm font-medium">{data.subject || '(no subject)'}</div>
            {data.bodyHtml ? (
              <EmailHtmlBody html={data.bodyHtml} messageId={data.messageId} />
            ) : (
              <pre className="whitespace-pre-wrap break-words text-sm">{data.bodyText}</pre>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
